import React, { useState, Fragment } from 'react';
import { useNavigate } from "react-router-dom";
import { partialUpdateFiling } from '../../../../../api/filings/filingAPI';
import { Handler401 } from '../../../../../utils/auth/authHandler';
import { submitFilingReview } from '../../../../../api/filings/reviewAPI';
import ModalNew from '../../../../../components/ui/popup/Modal';
import { PopupContent, PopupHeader } from '../../../../../components/ui/popup/common';
import LoadingPlaceholder from '../../../../../components/ui/loading/LoadingPlaceholder';

const CustomerReviewModal = ({ 
  showIntro, 
  showQuestions, 
  filingUuid, 
  onClose, 
  setFiling,
  onTransitionToQuestions // Function to toggle the parent state from Intro -> Questions
}) => {
  const navigate = useNavigate();
  const [introRating, setIntroRating] = useState(0);
  const [comments, setComments] = useState({ q1: "", q2: "", q3: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(null); // 'success', 'error'
  const [submissionMessage, setSubmissionMessage] = useState("");

  if (!showIntro && !showQuestions) return null;

  // STEP 1: Handle Skip (from Intro)
  const handleSkip = async () => {
    try {
      const response = await partialUpdateFiling("tax/filing", {
        review: {
          rated: introRating,
          review_skipped: true,
        },
      }, filingUuid);

      if (response.status === 401) return Handler401(navigate);

      if (response.ok) {
        setFiling((prev) => ({ ...prev, review_skiped: true, rated: introRating }));
        onClose();
      }
    } catch (error) {
      console.error("Error saving review skip:", error);
    }
  };

  // STEP 2: Handle Final Submission
  const handleFinalSubmit = async () => {
    setIsSubmitting(true);
    setSubmissionStatus(null);

    try {
      const questions = [
        {
          question_description: "How easy was it to complete each step of your filing process in Plaintr?",
          answer: { is_rated: false, answer_description: comments.q1 },
        },
        {
          question_description: "Was there anything confusing or unclear during your experience?",
          answer: { is_rated: false, answer_description: comments.q2 },
        },
        {
          question_description: "How smooth did the communication & approval process feel?",
          answer: { is_rated: false, answer_description: comments.q3 },
        },
      ];

      const response = await submitFilingReview(filingUuid, questions, introRating > 0 ? introRating : null);

      if (response.status === 401) return Handler401(navigate);

      if (response.ok) {
        setSubmissionStatus("success");
        setSubmissionMessage("Your feedback has been submitted successfully.");
      } else {
        const errorData = await response.json().catch(() => ({}));
        setSubmissionStatus("error");
        setSubmissionMessage(errorData.message || "Failed to submit review.");
      }
    } catch (err) {
      setSubmissionStatus("error");
      setSubmissionMessage("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalNew className="content-modal" style={{ maxWidth: showIntro ? "480px" : "520px" }}>
      <PopupHeader
        title={showIntro ? "Process Completed" : "Feedback"}
        setOpenState={onClose}
        icon={`${process.env.PUBLIC_URL}/images/icons/survey_blue_popup.svg`}
        variant="compact"
        hideCloseButton={true}
      />
      <PopupContent>
        {showIntro ? (
          /* --- STEP 1: INTRO --- */
          <div className="review-modal-intro-content">
            <p className="review-modal-intro-text">
              Your tax filing is complete! We'd love to hear about your experience. 
              Your feedback helps us improve and serve you better.
            </p>
            <div className="review-modal-intro-rating">
              <h5>Rate the Process</h5>
              <div className="rating review-modal-rating">
                {[5, 4, 3, 2, 1].map((star) => (
                  <Fragment key={star}>
                    <input type="radio" id={`intro-star${star}`} name="intro-rate" value={star} 
                           checked={introRating === star} onChange={(e) => setIntroRating(parseInt(e.target.value))} />
                    <label className="mFont" htmlFor={`intro-star${star}`}>star</label>
                  </Fragment>
                ))}
              </div>
            </div>
            <div className="review-modal-actions">
              <button type="button" className="cta-button primary" onClick={onTransitionToQuestions}>
                Provide Feedback
              </button>
              <button type="button" className="cta-button secondary" onClick={handleSkip}>
                Skip
              </button>
            </div>
          </div>
        ) : (
          /* --- STEP 2: QUESTIONS & STATUS --- */
          <>
            {!isSubmitting && !submissionStatus ? (
              <>
                <div className="review-modal-question">
                  <label className="review-modal-label-primary">How easy was it to complete each step?</label>
                  <textarea value={comments.q1} onChange={(e) => setComments({...comments, q1: e.target.value})} className="review-modal-textarea" />
                </div>
                <div className="review-modal-question">
                  <label className="review-modal-label-primary">Was there anything confusing?</label>
                  <textarea value={comments.q2} onChange={(e) => setComments({...comments, q2: e.target.value})} className="review-modal-textarea" />
                </div>
                <div className="review-modal-question">
                  <label className="review-modal-label-primary">How smooth was communication?</label>
                  <textarea value={comments.q3} onChange={(e) => setComments({...comments, q3: e.target.value})} className="review-modal-textarea" />
                </div>
                <div className="modal-actions" style={{ display: "flex", justifyContent: "center", gap: "12px" }}>
                  <button type="button" className="cta-button primary short" onClick={handleFinalSubmit}
                          disabled={!comments.q1.trim() || !comments.q2.trim() || !comments.q3.trim()}>
                    Submit Feedback
                  </button>
                </div>
              </>
            ) : isSubmitting ? (
              <div className="review-modal-loading"><LoadingPlaceholder rows={3} type="line" /></div>
            ) : (
              <div className="review-modal-status">
                <p className={`review-modal-status-title ${submissionStatus}`}>
                  {submissionStatus === 'success' ? "✓ Thanks!" : "✗ Error"}
                </p>
                <p className="review-modal-status-message">{submissionMessage}</p>
                <button type="button" className="cta-button primary short" onClick={onClose}>Close</button>
              </div>
            )}
          </>
        )}
      </PopupContent>
    </ModalNew>
  );
};

export default CustomerReviewModal;